export interface StudioSkillSelectionSource {
  id?: string;
  name: string;
  displayName?: string | null;
  scope?: string | null;
}

function key(value: string): string {
  return value.trim().toLowerCase();
}

function slugifyLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return slug || null;
}

export function studioSkillSelectionAliases(
  skill: StudioSkillSelectionSource,
): string[] {
  const aliases = new Set<string>();
  aliases.add(skill.name);
  if (skill.name.startsWith('system.')) {
    aliases.add(skill.name.slice('system.'.length));
  }
  const displaySlug = slugifyLabel(skill.displayName);
  if (displaySlug) aliases.add(displaySlug);
  return Array.from(aliases);
}

function resolveSavedSkill(
  value: string,
  skills: StudioSkillSelectionSource[],
): StudioSkillSelectionSource | null {
  const wanted = key(value);
  const exact = skills.find((skill) => key(skill.name) === wanted);
  if (exact) return exact;
  return (
    skills.find((skill) =>
      studioSkillSelectionAliases(skill).some((alias) => key(alias) === wanted),
    ) ?? null
  );
}

export function isStudioSkillSelected(
  selected: Iterable<string>,
  skill: StudioSkillSelectionSource,
  allSkills: StudioSkillSelectionSource[] = [skill],
): boolean {
  for (const value of selected) {
    const resolved = resolveSavedSkill(value, allSkills);
    if (resolved) {
      if (resolved.name === skill.name) return true;
      continue;
    }
    if (studioSkillSelectionAliases(skill).some((alias) => key(alias) === key(value))) {
      return true;
    }
  }
  return false;
}

export function toggleStudioSkillSelection(
  selected: Iterable<string>,
  skill: StudioSkillSelectionSource,
  allSkills: StudioSkillSelectionSource[] = [skill],
): string[] {
  const aliases = new Set(studioSkillSelectionAliases(skill).map(key));
  const currentlySelected = isStudioSkillSelected(selected, skill, allSkills);
  const next: string[] = [];
  const seen = new Set<string>();

  for (const value of selected) {
    const resolved = resolveSavedSkill(value, allSkills);
    const belongsToSkill = resolved
      ? resolved.name === skill.name
      : aliases.has(key(value));
    if (belongsToSkill) continue;
    const dedupeKey = key(value);
    if (!seen.has(dedupeKey)) {
      seen.add(dedupeKey);
      next.push(value);
    }
  }

  if (!currentlySelected && !seen.has(key(skill.name))) {
    next.push(skill.name);
  }

  return next;
}

export function normalizeStudioSkillSelections(
  selected: Iterable<string>,
  skills: StudioSkillSelectionSource[],
): string[] {
  const next: string[] = [];
  const seen = new Set<string>();
  for (const value of selected) {
    const resolved = resolveSavedSkill(value, skills);
    const canonical = resolved?.name ?? value;
    const canonicalKey = key(canonical);
    if (!seen.has(canonicalKey)) {
      seen.add(canonicalKey);
      next.push(canonical);
    }
  }
  return next;
}
